import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, Output, Renderer2 } from '@angular/core';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { CsCardInterfaceClass } from 'components/cs-card/cs-card';
import { CsCardListInterfaceClass } from 'components/cs-card-list/cs-card-list-interface';
import { DestroyGuard, getTransitionSlideInOutMs } from 'services/browser-utils';
import { TranslatedString } from 'models/translated-string';

export type CsInfoCardType = 'info' | 'warning' | 'error' | 'success' | 'checkmark' | 'translucent';

@Component({
    selector: 'cs-info-card',
    templateUrl: 'cs-info-card.html',
    providers: [{ provide: CsCardInterfaceClass, useExisting: CsInfoCardComponent }],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
      'class': 'info-card'
    }
})
export class CsInfoCardComponent extends CsCardInterfaceClass implements AfterViewInit, OnDestroy {
  @Input() type: CsInfoCardType = 'info';
  @Input() message?: TranslatedString;
  @Input() dismissible: boolean = true;
  @Input() hideIcon: boolean = false;
  @Input() actionText?: TranslatedString;
  @Output() dismissed = new EventEmitter<void>();
  @Output() actionClick = new EventEmitter<void>();

  @HostBinding('class.info-card--info')
  get isInfo() { return this.type === 'info'; }

  @HostBinding('class.info-card--warning')
  get isWarning() { return this.type === 'warning'; }

  @HostBinding('class.info-card--error')
  get isError() { return this.type === 'error'; }

  @HostBinding('class.info-card--success')
  get isSuccess() { return this.type === 'success'; }

  @HostBinding('class.info-card--checkmark')
  get isCheckmark() { return this.type === 'checkmark'; }

  @HostBinding('class.info-card--translucent')
  get isTranslucent() { return this.type === 'translucent'; }

  @HostBinding('class.info-card--mobile')
  get isMobile() { return hasClientSmallScreen(); }

  @HostBinding('class.info-card--desktop')
  get isDesktop() { return !hasClientSmallScreen(); }

  @HostBinding('class.info-card--centered')
  @Input() centered: boolean = false;

  @HostBinding('class.info-card--has-action')
  get hasAction() { return !!this.actionText; }

  @HostBinding('class.info-card--entered')
  entered = false;

  @HostBinding('class.info-card--leaving')
  leaving = false;

  // set for a "late insert" (card added after the list's initial layout) so the
  // fade-in runs via a keyframe instead of the transition the shared list
  // `.animate` rule would otherwise restrict to transform only.
  @HostBinding('class.info-card--deferred-enter')
  deferredEnter = false;

  private heightCache: number = 0;
  private resizeObserver: ResizeObserver | undefined;
  private destroyGuard = new DestroyGuard();
  // setList is called on every list mutation; this makes sure we schedule the
  // enter animation only once.
  private enterScheduled = false;

  constructor(
    elemRef: ElementRef,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef
  ) {
    super(elemRef);
  }

  override setList(list: CsCardListInterfaceClass): void {
    super.setList(list);

    // only schedule the enter animation the first time we're added to a list
    if (this.entered || this.enterScheduled) return;
    this.enterScheduled = true;

    // if this card is inserted into an already-initialized list, it is a "late
    // insert" (e.g. an info card that appears after async data loads). The
    // surrounding cards first animate to make space; delay this card's fade-in
    // by the slide duration so the space opens before the card appears, and use
    // the deferred-enter keyframe fade (see scss). On the initial list render
    // there is no reflow to wait for, so just enter on the next frame.
    if (list && !list.initializing) {
      this.destroyGuard.safeTimeout(() => {
        if (this.destroyGuard.isDestroyed) return;
        this.deferredEnter = true;
        this.entered = true;
        // zoneless + OnPush: the mutation needs explicit CD to write the host class
        this.cdr.markForCheck();
      }, getTransitionSlideInOutMs());
    } else {
      requestAnimationFrame(() => {
        if (this.destroyGuard.isDestroyed) return;
        this.entered = true;
        this.cdr.markForCheck();
      });
    }
  }

  ngAfterViewInit(): void {
    // Set up ResizeObserver to track height changes
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateHeight();
      });
      this.resizeObserver.observe(this.elemRef.nativeElement);
    }

    // Initial height calculation
    setTimeout(() => {
      this.updateHeight();
      if (this.list) {
        this.list.updatePositions();
      }
    }, 0);

  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.destroyGuard.destroy();
  }

  updateLayout(): void {
    // Called by card list to update layout
    this.updateHeight();
  }

  private updateHeight(): void {
    if (!this.elemRef || !this.elemRef.nativeElement) {
      return;
    }

    const element = this.elemRef.nativeElement as HTMLElement;
    const newHeight = element.offsetHeight;

    if (newHeight > 0 && newHeight !== this.heightCache) {
      const oldHeight = this.heightCache;
      this.heightCache = newHeight;
      // Set CSS variables for animations
      element.style.setProperty('--current-height', `${newHeight}`);
      element.style.setProperty('--current-height-px', `${newHeight}px`);

      // Notify list of height change only if height actually changed
      if (this.list && oldHeight !== newHeight) {
        // Defer to avoid sync updates
        requestAnimationFrame(() => {
          this.list.updatePositions();
        });
      }
    }
  }

  getCurrentCardHeight(): number {
    // Return cached height or calculate fresh
    if (this.heightCache > 0) {
      return this.heightCache;
    }

    if (this.elemRef && this.elemRef.nativeElement) {
      const height = this.elemRef.nativeElement.offsetHeight;
      if (height > 0) {
        this.heightCache = height;
      }
      return height;
    }

    return 0;
  }

  getCardElement(): HTMLElement {
    return this.elemRef.nativeElement;
  }

  getIconName(): string {
    switch (this.type) {
      case 'info':
        return 'information-circle';
      case 'warning':
        return 'information-circle';
      case 'error':
        return 'warning';
      case 'success':
        return 'thumbs-up';
      case 'checkmark':
        return 'checkmark-circle';
      default:
        return 'information-circle';
    }
  }

  hasClientSmallScreen(): boolean {
    return hasClientSmallScreen();
  }

  onActionClick(event: Event): void {
    event.stopPropagation();
    this.actionClick.emit();
  }

  onClose(event: Event): void {
    event.stopPropagation();
    const element = this.elemRef.nativeElement as HTMLElement;

    // if inside a card list, animate out first
    if (this.list && element) {
      // capture current Y position from inline transform before adding leaving class
      // format is "translate(Xpx, Ypx)"
      const currentTransform = element.style.transform;
      const match = currentTransform.match(/translate\(([^,]+),\s*([^)]+)\)/);
      const currentY = match ? match[2] : '0px';

      // store Y position as CSS variable for the leaving animation
      element.style.setProperty('--leave-y', currentY);

      this.leaving = true;
      // zoneless + OnPush: ensure the info-card--leaving host class actually lands in the DOM
      this.cdr.markForCheck();

      // wait for CSS transition to complete ($transition-slide-in-out), then hide and emit
      element.addEventListener('transitionend', () => {
        this.hideElement(element);
        this.dismissed.emit();
      }, { once: true });
    } else {
      // not in list, hide immediately (original behavior)
      if (element) {
        this.hideElement(element);
      }
      this.dismissed.emit();
    }
  }

  private hideElement(element: HTMLElement): void {
    this.renderer.setStyle(element, 'display', 'none');
    this.renderer.setStyle(element, 'height', '0');
    this.renderer.setStyle(element, 'margin', '0');
    this.renderer.setStyle(element, 'padding', '0');
    this.renderer.setStyle(element, 'overflow', 'hidden');

    if (this.list) {
      this.list.updatePositions();
    }
  }
}